import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  Image,
  HStack,
} from "@chakra-ui/react";
import { useGetSummaryQuery } from "services/api";

const SummaryTitle = ({ title }) => {
  return (
    <Box mb={4}>
      <Heading size="md">{title}</Heading>
    </Box>
  );
};

const FileLink = ({ fileName, onVideoClick, thumbnailUrl, videoId }) => {
  return (
    <HStack
      _hover={{
        bg: "gray.100",
        borderRadius: "md",
      }}
      cursor="pointer"
      onClick={() => onVideoClick(videoId)}
      p={1}
      spacing={2}
    >
      {!!thumbnailUrl && (
        <Image
          alt={fileName}
          borderRadius="md"
          boxSize="40px"
          objectFit="cover"
          src={thumbnailUrl}
        />
      )}
      <Text color="blue.500" fontSize="sm" fontWeight="medium">
        {fileName}
      </Text>
    </HStack>
  );
};

const SummarySection = ({
  descriptions,
  file_ids,
  files,
  name,
  onVideoClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef(null);

  const descriptionItems = Array.isArray(descriptions) ? descriptions : [];
  const fileItems = Array.isArray(files) ? files : [];
  const fileIdsMap = file_ids || {};

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = 20;
      const textHeight = textRef.current.scrollHeight;
      const visibleHeight = lineHeight * 3;
      setShowButton(textHeight > visibleHeight);
    }
  }, [descriptions, files]);

  return (
    <Box>
      <Heading mb={1} size="sm">
        {name}
      </Heading>
      <Box noOfLines={isExpanded ? undefined : 3} ref={textRef}>
        {descriptionItems.length > 0 &&
          descriptionItems.map((description, idx) => (
            <Text fontSize="sm" key={idx} mb={2}>
              {description}
            </Text>
          ))}

        {fileItems.length > 0 && (
          <VStack align="stretch" spacing={1} mb={2}>
            {fileItems.map((fileName, idx) => {
              if (!fileName) return null;
              const fileInfo = fileIdsMap[fileName] || {};
              const videoId = fileInfo.video_id || "";
              const thumbnailUrl = fileInfo.thumbnail_url || "";

              return (
                <FileLink
                  fileName={fileName}
                  key={idx}
                  onVideoClick={onVideoClick}
                  thumbnailUrl={thumbnailUrl}
                  videoId={videoId}
                />
              );
            })}
          </VStack>
        )}
      </Box>
      {!!showButton && (
        <Button
          colorScheme="blue"
          mt={1}
          onClick={() => setIsExpanded(!isExpanded)}
          size="xs"
          variant="ghost"
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const Summary = ({ onVideoClick }) => {
  const { projectId } = useParams();

  const { data: summaryData } = useGetSummaryQuery({
    projectId,
  });

  // Process the JSON summary data
  const processJsonSummary = (data) => {
    if (!data) return { title: "", sections: [] };

    try {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const title = parsedData.title || "Wedding Video Summary";
      const sections = [];

      // Process each section from the JSON data
      if (parsedData.sections && Array.isArray(parsedData.sections)) {
        parsedData.sections.forEach((section) => {
          sections.push({
            name: section.name || "",
            descriptions: Array.isArray(section.descriptions)
              ? section.descriptions
              : [],
            files: Array.isArray(section.files) ? section.files : [],
            file_ids: section.file_ids || {},
          });
        });
      }

      return { title, sections };
    } catch (error) {
      console.error("Error processing summary JSON:", error);
      return { title: "", sections: [] };
    }
  };

  const { title, sections } = processJsonSummary(summaryData);

  return (
    <Box pb="75px">
      <SummaryTitle title={title} />
      <VStack align="stretch" spacing={4}>
        {sections.map((section, index) => (
          <SummarySection
            descriptions={section.descriptions}
            file_ids={section.file_ids}
            files={section.files}
            key={index}
            name={section.name}
            onVideoClick={onVideoClick}
          />
        ))}
      </VStack>
    </Box>
  );
};

export default Summary;
